/**
 * Privacy-safe analytics boundary.
 *
 * Components may only emit the events defined below. Never add names, email
 * addresses, phone numbers, postal codes, messages, or other form values to an
 * analytics event.
 */

type PhoneClickEvent = {
  event: "phone_click";
  link_location: string;
};

type QuoteFormStartEvent = {
  event: "quote_form_start";
  form_id: string;
};

type QuoteFormSubmitEvent = {
  event: "quote_form_submit";
  form_id: string;
};

type ServiceCtaClickEvent = {
  event: "service_cta_click";
  cta_location: string;
  service: string;
};

type BeforeAfterInteractionEvent = {
  event: "before_after_interaction";
  project_id: string;
  interaction_method: "pointer" | "keyboard_or_input";
};

type LanguageChangeEvent = {
  event: "language_change";
  from_locale: string;
  to_locale: string;
  route_id: string;
};

type ServicePageViewEvent = {
  event: "service_page_view";
  service: string;
  locale: string;
};

type ProjectGalleryViewEvent = {
  event: "project_gallery_view";
  locale: string;
};

type EmergencyCtaClickEvent = {
  event: "emergency_cta_click";
  cta_location: string;
  lead_type: "emergency";
};

type EstimateCtaClickEvent = {
  event: "estimate_cta_click";
  cta_location: string;
  lead_type: "planned";
};

type EstimateStartedEvent = {
  event: "estimate_started";
  form_id: string;
  lead_type: "emergency" | "planned";
};

type EstimateCompletedEvent = {
  event: "estimate_completed";
  form_id: string;
  lead_type: "emergency" | "planned";
  property_type?: string;
};

type CityPageViewedEvent = {
  event: "city_page_viewed";
  city: string;
  locale: string;
};

type PhotoUploadedEvent = {
  /**
   * Required reporting event name. The file_status property distinguishes a
   * local selection from a completed binary upload; filenames and file bytes
   * must never enter analytics.
   */
  event: "photo_uploaded";
  form_id: string;
  lead_type: "emergency" | "planned";
  file_count: string;
  file_status: "selected_not_transmitted";
};

export type AnalyticsEvent =
  | PhoneClickEvent
  | QuoteFormStartEvent
  | QuoteFormSubmitEvent
  | ServiceCtaClickEvent
  | BeforeAfterInteractionEvent
  | LanguageChangeEvent
  | ServicePageViewEvent
  | ProjectGalleryViewEvent
  | EmergencyCtaClickEvent
  | EstimateCtaClickEvent
  | EstimateStartedEvent
  | EstimateCompletedEvent
  | CityPageViewedEvent
  | PhotoUploadedEvent;

type DataLayerItem = Record<string, unknown> | IArguments;

declare global {
  interface Window {
    dataLayer?: DataLayerItem[];
  }
}

const GTM_PATTERN = /^GTM-[A-Z0-9]+$/i;
const GA4_PATTERN = /^G-[A-Z0-9]+$/i;
const GOOGLE_ADS_PATTERN = /^AW-\d+$/i;
const CONVERSION_LABEL_PATTERN = /^[A-Za-z0-9_-]+$/;
const MAX_VALUE_LENGTH = 120;

const config = {
  gtmId: validEnv(import.meta.env.VITE_GTM_ID, GTM_PATTERN),
  ga4Id: validEnv(import.meta.env.VITE_GA4_MEASUREMENT_ID, GA4_PATTERN),
  googleAdsId: validEnv(import.meta.env.VITE_GOOGLE_ADS_ID, GOOGLE_ADS_PATTERN),
  quoteConversionLabel: validEnv(
    import.meta.env.VITE_GOOGLE_ADS_QUOTE_CONVERSION_LABEL,
    CONVERSION_LABEL_PATTERN,
  ),
  phoneConversionLabel: validEnv(
    import.meta.env.VITE_GOOGLE_ADS_PHONE_CONVERSION_LABEL,
    CONVERSION_LABEL_PATTERN,
  ),
};

let initialized = false;

function validEnv(value: string | undefined, pattern: RegExp): string | undefined {
  const trimmed = value?.trim();
  return trimmed && pattern.test(trimmed) ? trimmed : undefined;
}

function dataLayer(): DataLayerItem[] | undefined {
  if (typeof window === "undefined") return undefined;
  window.dataLayer = window.dataLayer ?? [];
  return window.dataLayer;
}

function gtag(..._args: unknown[]): void {
  const layer = dataLayer();
  if (!layer) return;
  // Google tag commands intentionally require the function's Arguments object.
  // eslint-disable-next-line prefer-rest-params
  layer.push(arguments);
}

function appendScript(id: string, src: string, marker: string): void {
  if (document.querySelector(`script[data-revolin-analytics="${marker}"]`)) return;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  script.dataset["revolinAnalytics"] = marker;
  document.head.appendChild(script);
}

/**
 * Initializes configured Google tags once. Consent defaults to denied; call
 * updateAnalyticsConsent(true) only after the visitor grants consent.
 *
 * When GTM is configured it owns downstream GA4/Ads tags. Without GTM, the
 * optional GA4 and Google Ads IDs are loaded directly.
 */
export function initializeAnalytics(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  dataLayer();

  gtag("consent", "default", {
    ad_storage: "denied",
    analytics_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });

  if (config.gtmId) {
    window.dataLayer!.push({ "gtm.start": Date.now(), event: "gtm.js" });
    appendScript(
      "revolin-gtm",
      `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(config.gtmId)}`,
      "gtm",
    );
    return;
  }

  const directId = config.ga4Id ?? config.googleAdsId;
  if (!directId) return;

  appendScript(
    "revolin-gtag",
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(directId)}`,
    "gtag",
  );
  gtag("js", new Date());
  if (config.ga4Id) gtag("config", config.ga4Id);
  if (config.googleAdsId) gtag("config", config.googleAdsId);
}

/** Updates Google Consent Mode after a consent choice. */
export function updateAnalyticsConsent(granted: boolean): void {
  initializeAnalytics();
  const value = granted ? "granted" : "denied";
  gtag("consent", "update", {
    ad_storage: value,
    analytics_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  });
}

/** Emits one allowlisted, non-PII event to GTM or configured direct tags. */
export function trackAnalyticsEvent(event: AnalyticsEvent): void {
  if (typeof window === "undefined") return;
  initializeAnalytics();

  const payload = sanitizeEvent(event);

  if (config.gtmId || (!config.ga4Id && !config.googleAdsId)) {
    dataLayer()?.push(payload);
  } else {
    const { event: eventName, ...parameters } = payload;
    gtag("event", eventName, parameters);
  }

  if (!config.gtmId && config.googleAdsId) {
    const conversionLabel =
      event.event === "quote_form_submit"
        ? config.quoteConversionLabel
        : event.event === "phone_click"
          ? config.phoneConversionLabel
          : undefined;

    if (conversionLabel) {
      gtag("event", "conversion", {
        send_to: `${config.googleAdsId}/${conversionLabel}`,
      });
    }
  }
}

function sanitizeEvent(event: AnalyticsEvent): Record<string, string> {
  const pagePath = window.location.pathname.slice(0, MAX_VALUE_LENGTH);
  const payload: Record<string, string> = {
    event: event.event,
    page_path: pagePath,
  };

  for (const [key, value] of Object.entries(event)) {
    if (key === "event") continue;
    payload[key] = value.slice(0, MAX_VALUE_LENGTH);
  }
  return payload;
}
